/**
 * Density State Hook
 *
 * Manages view density state for Responses view (comfortable, compact, stacked).
 * Persists selection in localStorage and URL hash for deep linking.
 * URL hash takes precedence for shared links: #density=stacked|comfortable|compact
 */

import { useState, useCallback, useEffect } from 'react';
import { logger } from '../../../lib/logging';

export type DensityOption = 'comfortable' | 'compact' | 'stacked';

interface DensityState {
  density: DensityOption;
  setDensity: (density: DensityOption) => void;
}

const STORAGE_KEY = 'results_density';
const DEFAULT_DENSITY: DensityOption = 'stacked';
const MOBILE_BREAKPOINT_QUERY = '(max-width: 767px)';

function isMobileViewport(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  try {
    return window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches;
  } catch (error) {
    logger.warn('density_mobile_match_failed', undefined, {
      op: 'density_mobile_match_failed',
      component: 'density',
      error: error instanceof Error ? error.message : String(error)
    });
    return false;
  }
}

/**
 * Get density from localStorage with fallback to default
 */
function getDensityFromStorage(): DensityOption {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'stacked' || stored === 'comfortable' || stored === 'compact') {
      return stored as DensityOption;
    }
  } catch (error) {
    logger.warn('density_storage_read_failed', undefined, {
      op: 'density_storage_read_failed',
      component: 'density',
      error: error instanceof Error ? error.message : String(error)
    });
  }
  return DEFAULT_DENSITY;
}

/**
 * Get density from URL hash
 */
function getDensityFromHash(): DensityOption | null {
  const hash = window.location.hash.slice(1);
  const params = new URLSearchParams(hash);
  const density = params.get('density');

  if (density === 'stacked' || density === 'comfortable' || density === 'compact') {
    return density as DensityOption;
  }
  return null;
}

/**
 * Set density in URL hash
 */
function setDensityInHash(density: DensityOption): void {
  const hash = window.location.hash.slice(1);
  const params = new URLSearchParams(hash);
  params.set('density', density);
  window.location.hash = params.toString();
}

export const useDensity = (): DensityState => {
  // Initialize from URL hash (priority) or localStorage, with mobile override
  const [density, setDensityState] = useState<DensityOption>(() => {
    if (isMobileViewport()) {
      return DEFAULT_DENSITY;
    }
    return getDensityFromHash() ?? getDensityFromStorage();
  });
  const [isMobile, setIsMobile] = useState<boolean>(() => isMobileViewport());

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQuery = window.matchMedia(MOBILE_BREAKPOINT_QUERY);

    const updateIsMobile = (matches: boolean) => {
      setIsMobile(matches);
    };

    updateIsMobile(mediaQuery.matches);

    const listener = (event: MediaQueryListEvent) => {
      updateIsMobile(event.matches);
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', listener);
      return () => mediaQuery.removeEventListener('change', listener);
    }

    mediaQuery.addListener(listener);
    return () => mediaQuery.removeListener(listener);
  }, []);

  const setDensity = useCallback((newDensity: DensityOption) => {
    const startTime = performance.now();
    const targetDensity = isMobile ? 'stacked' : newDensity;

    setDensityState(targetDensity);

    // Persist to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, targetDensity);
    } catch (error) {
      logger.warn('density_storage_write_failed', undefined, {
        op: 'density_storage_write_failed',
        component: 'density',
        error: error instanceof Error ? error.message : String(error)
      });
    }

    // Persist to URL hash
    setDensityInHash(targetDensity);

    logger.info('density_set', undefined, {
      op: 'density_set',
      component: 'density',
      value: targetDensity,
      duration_ms: performance.now() - startTime,
      persisted: true,
      forcedMobile: isMobile && newDensity !== 'stacked'
    });
  }, [isMobile]);

  // Listen for hash changes (back/forward navigation)
  useEffect(() => {
    const handleHashChange = () => {
      const newDensity = getDensityFromHash();
      if (newDensity) {
        if (isMobile && newDensity !== 'stacked') {
          setDensity('stacked');
          logger.info('density_mobile_override', undefined, {
            op: 'density_mobile_override',
            component: 'density',
            requested: newDensity,
            forced: 'stacked',
            source: 'hash_change'
          });
          return;
        }

        if (!isMobile) {
          setDensityState(newDensity);

          logger.info('density_restored', undefined, {
            op: 'density_restored',
            component: 'density',
            value: newDensity,
            source: 'hash_change'
          });
        }
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [isMobile, setDensity]);

  useEffect(() => {
    if (isMobile) {
      setDensity('stacked');
    } else {
      const storedDensity = getDensityFromHash() ?? getDensityFromStorage();
      setDensity(storedDensity);
    }
  }, [isMobile, setDensity]);

  return {
    density,
    setDensity
  };
};
